import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

const monoFont = "'IBM Plex Mono', monospace";

interface Props {
  onClose: () => void;
  onDone: () => void;
}

export function BulkInviteModal({ onClose, onDone }: Props) {
  const [quantity, setQuantity] = useState(5);
  const [plan, setPlan] = useState('free');
  const [channel, setChannel] = useState('');
  const [generating, setGenerating] = useState(false);
  const [links, setLinks] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    setError('');
    const qty = Math.min(Math.max(1, quantity), 50);
    setGenerating(true);

    const { data: session } = await supabase.auth.getSession();
    const rows: any[] = [];
    const generatedLinks: string[] = [];

    for (let i = 0; i < qty; i++) {
      const token = crypto.randomUUID();
      rows.push({
        token,
        plan,
        invited_by: session.session?.user.id,
        ...(channel.trim() ? { channel: channel.trim() } : {}),
      });
      generatedLinks.push(`${window.location.origin}/convite/${token}`);
    }

    const { error: insertError } = await supabase.from('invites').insert(rows);
    if (insertError) {
      setError('Erro ao gerar convites.');
      setGenerating(false);
      return;
    }

    setLinks(generatedLinks);
    setGenerating(false);
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(links.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
    letterSpacing: '0.08em', color: 'rgba(247,246,242,0.5)', marginBottom: 6, display: 'block',
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 14, fontWeight: 300, width: '100%',
    padding: '10px 12px', border: '1px solid rgba(247,246,242,0.1)',
    backgroundColor: 'rgba(247,246,242,0.04)', color: 'rgba(247,246,242,0.85)',
  };

  const planBtnStyle = (isActive: boolean): React.CSSProperties => ({
    fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
    padding: '6px 14px', border: `1px solid ${isActive ? 'rgba(247,246,242,0.4)' : 'rgba(247,246,242,0.1)'}`,
    backgroundColor: isActive ? 'rgba(247,246,242,0.1)' : 'transparent',
    color: isActive ? '#F7F6F2' : 'rgba(247,246,242,0.4)', cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.7)',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#1a1a1a', padding: 32, maxWidth: 480, width: '100%' }}>
        {links.length > 0 ? (
          <div>
            <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(247,246,242,0.4)', marginBottom: 16 }}>
              LINKS GERADOS ({links.length})
            </div>
            <div style={{
              fontFamily: monoFont, fontSize: 12, fontWeight: 300, padding: '12px',
              backgroundColor: 'rgba(247,246,242,0.04)', border: '1px solid rgba(247,246,242,0.1)',
              color: 'rgba(247,246,242,0.6)', maxHeight: 300, overflowY: 'auto',
              display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16,
            }}>
              {links.map((l, i) => (
                <div key={i} style={{ wordBreak: 'break-all' }}>{l}</div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={handleCopyAll}
                style={{
                  fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
                  padding: '8px 16px', border: '1px solid rgba(247,246,242,0.3)',
                  backgroundColor: 'transparent', color: copied ? '#00E87A' : '#F7F6F2', cursor: 'pointer',
                }}>
                {copied ? 'COPIADO ✓' : 'COPIAR TODOS'}
              </button>
              <button onClick={onDone}
                style={{
                  fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
                  padding: '8px 16px', border: '1px solid rgba(247,246,242,0.15)',
                  backgroundColor: 'transparent', color: 'rgba(247,246,242,0.5)', cursor: 'pointer',
                }}>
                FECHAR
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(247,246,242,0.4)', marginBottom: 24 }}>
              GERAR LINKS EM MASSA
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Quantidade (máximo 50)</label>
                <input type="number" min={1} max={50} value={quantity}
                  onChange={e => setQuantity(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
                  style={{ ...inputStyle, width: 100 }} />
              </div>
              <div>
                <label style={labelStyle}>Plano para todos</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['free', 'pro', 'custom'].map(p => (
                    <button key={p} onClick={() => setPlan(p)} style={planBtnStyle(plan === p)}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Canal de origem (opcional)</label>
                <input value={channel} onChange={e => setChannel(e.target.value)} style={inputStyle} placeholder="ex: evento fotográfico SP" />
              </div>
              {error && <div style={{ fontFamily: monoFont, fontSize: 12, color: '#FF3F2D' }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={onClose}
                  style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '8px 16px', border: '1px solid rgba(247,246,242,0.15)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.5)', cursor: 'pointer' }}>
                  CANCELAR
                </button>
                <button onClick={handleGenerate} disabled={generating}
                  style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '8px 16px', border: '1px solid rgba(247,246,242,0.3)', backgroundColor: 'transparent', color: '#F7F6F2', cursor: generating ? 'wait' : 'pointer' }}>
                  {generating ? 'GERANDO...' : 'GERAR LINKS'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
